"use client"
import { useRef, useEffect } from 'react'
import * as THREE from 'three';

interface SphereProps {
  size?: number;
  className?: string;
  gradientUrl?: string;
}

export default function Sphere({ 
  size = 84, 
  className = "",
  gradientUrl = '/Gradient.svg'
}: SphereProps) {
  const mountRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (!mountRef.current) return;

    // Fixed dimensions
    const width = size
    const height = size

    // 1) scene + camera + renderer
    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(45, width/height, 0.1, 100)
    camera.position.set(0, 0, 3)

    const renderer = new THREE.WebGLRenderer({ 
      antialias: true, 
      alpha: true,
      canvas: canvasRef.current || undefined
    })
    renderer.setSize(width, height)
    renderer.setClearColor(0x000000, 0) // Transparent background
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)) // Better quality on high DPI screens
    
    if (!canvasRef.current) {
      mountRef.current.appendChild(renderer.domElement)
      canvasRef.current = renderer.domElement
    }

    // ✨ Use SphereGeometry to create a sphere shape.
    const geo = new THREE.SphereGeometry(1, 64, 64)
    
    const uniforms = {
      uTime: { value: 0 },
      uGradient: { value: new THREE.TextureLoader().load(gradientUrl) }
    }

    const mat = new THREE.ShaderMaterial({
      uniforms,
      vertexShader: `
        uniform float uTime;
        varying vec2 vUv;
        
        // Simplex noise function
        vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
        vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
        float snoise(vec3 v) { 
          const vec2 C = vec2(1.0/6.0, 1.0/3.0) ;
          const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
          vec3 i  = floor(v + dot(v, C.yyy) );
          vec3 x0 = v - i + dot(i, C.xxx) ;
          vec3 g = step(x0.yzx, x0.xyz);
          vec3 l = 1.0 - g;
          vec3 i1 = min( g.xyz, l.zxy );
          vec3 i2 = max( g.xyz, l.zxy );
          vec3 x1 = x0 - i1 + C.xxx;
          vec3 x2 = x0 - i2 + C.yyy;
          vec3 x3 = x0 - D.yyy;
          i = mod289(i); 
          vec4 p = permute( permute( permute( 
                     i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
                   + i.y + vec4(0.0, i1.y, i2.y, 1.0 )) 
                   + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));
          float n_ = 0.142857142857;
          vec3  ns = n_ * D.wyz - D.xzx;
          vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
          vec4 x_ = floor(j * ns.z);
          vec4 y_ = floor(j - 7.0 * x_ );
          vec4 x = x_ * ns.x + ns.yyyy;
          vec4 y = y_ * ns.x + ns.yyyy;
          vec4 h = 1.0 - abs(x) - abs(y);
          vec4 b0 = vec4( x.xy, y.xy );
          vec4 b1 = vec4( x.zw, y.zw );
          vec4 s0 = floor(b0)*2.0 + 1.0;
          vec4 s1 = floor(b1)*2.0 + 1.0;
          vec4 sh = -step(h, vec4(0.0));
          vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
          vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;
          vec3 p0 = vec3(a0.xy,h.x);
          vec3 p1 = vec3(a0.zw,h.y);
          vec3 p2 = vec3(a1.xy,h.z);
          vec3 p3 = vec3(a1.zw,h.w);
          vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
          p0 *= norm.x;
          p1 *= norm.y;
          p2 *= norm.z;
          p3 *= norm.w;
          vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
          m = m * m;
          return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1), 
                                        dot(p2,x2), dot(p3,x3) ) );
        }

        void main(){
          vUv = uv;
          float n = snoise(normal * 2. + position*3. + uTime*0.5);
          vec3 pos = position + normal * n * 0.2;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(pos,1.);
        }
      `,
      fragmentShader: `
        uniform sampler2D uGradient;
        varying vec2 vUv;
        void main(){
          vec3 color = texture2D(uGradient, vec2(vUv.y,0.5)).rgb;
          gl_FragColor = vec4(color,1.);
        }
      `,
      transparent: true
    })

    const mesh = new THREE.Mesh(geo, mat)
    scene.add(mesh)

    const clock = new THREE.Clock()
    let animationFrameId: number;

    const tick = () => {
      mat.uniforms.uTime.value = clock.getElapsedTime()
      renderer.render(scene, camera)
      animationFrameId = requestAnimationFrame(tick)
    }
    tick()

    const currentMount = mountRef.current;
    return () => {
      cancelAnimationFrame(animationFrameId)
      renderer.dispose()
      if (currentMount && canvasRef.current) {
        currentMount.removeChild(canvasRef.current)
      }
    }
  }, [size, gradientUrl])

  return (
    <div
      ref={mountRef}
      className={className}
      style={{ 
        width: `${size}px`, 
        height: `${size}px`, 
        position: 'relative',
        borderRadius: '50%',
        overflow: 'hidden',
        display: 'inline-block',
        background: 'transparent'
      }}
    />
  )
}

// Example usage component
export function SphereExample() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-900 gap-8">
      <div className="text-center">
        <h3 className="text-white mb-4">84x84 Sphere</h3>
        <Sphere size={84} className="border border-gray-600" />
      </div>
      
      <div className="text-center">
        <h3 className="text-white mb-4">120x120 Sphere</h3>
        <Sphere size={120} className="border border-blue-400" />
      </div>
      
      <div className="text-center">
        <h3 className="text-white mb-4">60x60 Sphere</h3>
        <Sphere size={60} className="border border-pink-400" />
      </div>
    </div>
  )
}